import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../http-client';

// --- Query Key Factory ---
export const dashboardDataKeys = {
  all: ['dashboard-data'] as const,
  totalUsers: () => [...dashboardDataKeys.all, 'total-users'] as const,
  activeOrganizations: () =>
    [...dashboardDataKeys.all, 'active-organizations'] as const,
  systemHealth: () => [...dashboardDataKeys.all, 'system-health'] as const,
  activeJackpots: () =>
    [...dashboardDataKeys.all, 'active-jackpots'] as const,
  recentEvents: (days?: number) =>
    [...dashboardDataKeys.all, 'recent-events', days] as const,
  memberStatistics: () =>
    [...dashboardDataKeys.all, 'member-statistics'] as const,
  myUpcomingEvents: () =>
    [...dashboardDataKeys.all, 'my-upcoming-events'] as const,
  myUpcomingRodeos: () =>
    [...dashboardDataKeys.all, 'my-upcoming-rodeos'] as const,
  myClosingRodeos: (days?: number) =>
    days
      ? ([...dashboardDataKeys.all, 'my-closing-rodeos', days] as const)
      : ([...dashboardDataKeys.all, 'my-closing-rodeos'] as const),
  myEventHistory: (params?: Omit<EventHistoryParams, 'enabled'>) =>
    [...dashboardDataKeys.all, 'my-event-history', params] as const,
  myPendingInvitations: () =>
    [...dashboardDataKeys.all, 'my-pending-invitations'] as const,
};

/**
 * Team-roping partner invitation awaiting the current user's response.
 * Returned by `/dashboard-data/my/pending-invitations` and rendered as
 * the top-of-dashboard "needs your action" card. Cancelled here means
 * the row backs onto the same accept/decline mutation
 * (`useRespondToPartner`) that the EventDetailPage already wires up, so
 * inline buttons stay behavior-aligned across surfaces.
 */
export interface PendingInvitation {
  registration_id: string;
  position: string | null;
  /** event_registration_statuses.name, e.g. 'pending_confirmation'. */
  registration_status: string | null;
  /**
   * True when the team primary is covering the partner's entry fee
   * (Pay Later path). Drives the "accept = free seat" vs "accept =
   * pending payment" copy.
   */
  partner_covers_payment: boolean;
  inviter_name: string | null;
  invited_at: string | null;
  event: {
    id: string;
    event_name: string | null;
  } | null;
  rodeo: {
    id: string;
    name: string | null;
    start_date: string | null;
    end_date: string | null;
    entry_close_time: string | null;
    rodeo_or_jackpot: string | null;
    timezone: string | null;
  } | null;
}

// --- Types ---
interface TotalUsersData {
  total: number;
}

interface ActiveOrganizationsData {
  total: number;
}

interface SystemHealthData {
  active_sessions: number;
  uptime_seconds: number;
  [key: string]: unknown;
}

interface ActiveJackpotsData {
  count: number;
  jackpots: Array<{
    id: string;
    name: string;
    organization_id: string;
    [key: string]: unknown;
  }>;
}

interface RecentEvent {
  id: string;
  event_name: string;
  start_date?: string;
  [key: string]: unknown;
}

interface MemberStatistics {
  total_members: number;
  active_members: number;
  [key: string]: unknown;
}

interface UpcomingEvent {
  id: string;
  event_name: string;
  start_date: string;
  position: string;
  /** PayPal | cash | etc. — sourced from the linked payment row. */
  payment_method?: string | null;
  /** completed | pending | refunded | etc. */
  payment_status?: string | null;
  payment_amount?: number | string | null;
  non_member_fee?: number | string | null;
  paid_for_partner?: boolean | null;
  /**
   * Partner-side enrichment populated server-side (dashboard.service
   * `getUpcomingEvents`). Drives the team-roping partner-status block on
   * the dashboard card: identity, invitation acceptance, and per-side
   * payment state.
   */
  partner_name?: string | null;
  partner_status?: string | null;
  partner_user_profile_id?: string | null;
  partner_registration_id?: string | null;
  registration_type?: 'individual' | 'team_primary' | 'team_partner' | null;
  partner_payment_amount?: number | string | null;
  partner_non_member_fee?: number | string | null;
  /** Partner registration status name (e.g. 'paid', 'pending_payment'). */
  partner_registration_status?: string | null;
  /** Partner payment record status (e.g. 'completed', 'pending'). */
  partner_payment_status?: string | null;
  /** Partner payment provider (paypal/stripe/cash). */
  partner_payment_method?: string | null;
  /**
   * User's chosen payment method on THIS registration row (e.g.
   * 'pay_later', 'cash', 'stripe', 'paypal'). Distinct from
   * `payment_method` above which reflects the linked payment row's
   * provider — when a user picks pay-later their registration is
   * payment_method='pay_later' but a stub stripe/paypal payment row
   * gets created so they can settle later.
   */
  selected_payment_method?: string | null;
  /** Partner's chosen payment method on their registration row. */
  partner_selected_payment_method?: string | null;
  [key: string]: unknown;
}

/**
 * Per-member event entry surfaced under a buddy group on the dashboard.
 * One row per active event_registration the member has at the rodeo
 * (regardless of whether the registration row is itself flagged with
 * the buddy group ID — we show the member's full involvement at the
 * rodeo so the breakdown matches the user's mental model).
 *
 * Several fields encode parent-buddy-group context so the UI can flag
 * "side entries" (e.g. team-roping with a partner who isn't in the
 * group) and distinguish those from buddy-group-tied entries:
 *   - via_buddy_group: registration's buddy_group_id matches the parent
 *     group we're rendering it under.
 *   - partner_in_buddy_group: partner is also a member of the parent
 *     group (only meaningful for events with a partner).
 *   - preferences_match_buddy_group: the registration's preferences
 *     match the parent group's locked preferences. null when the group
 *     hasn't locked preferences yet (no comparison to make).
 */
export interface UpcomingBuddyGroupMemberEvent {
  registration_id: string;
  event_id: string;
  event_name: string;
  position: string | null;
  status: string;
  via_buddy_group: boolean;
  buddy_group_id: string | null;
  partner_name: string | null;
  partner_user_profile_id: string | null;
  partner_status: string | null;
  partner_in_buddy_group: boolean;
  first_performance_preference: string | null;
  second_performance_preference: string | null;
  preferences_match_buddy_group: boolean | null;
}

/**
 * One buddy-group member as rendered in the expandable breakdown.
 * `is_self` is true for the row owner so the UI can highlight them; the
 * service sorts members so the row owner appears first, then the primary
 * cardholder, then alphabetically.
 */
export interface UpcomingBuddyGroupMember {
  user_profile_id: string;
  display_name: string;
  is_self: boolean;
  is_primary: boolean;
  event_count: number;
  events: UpcomingBuddyGroupMemberEvent[];
}

/**
 * Buddy-group involvement record for a rodeo on the upcoming-rodeos
 * dashboard view. Each row is one buddy group the user belongs to that's
 * attached to the parent rodeo, with the rodeo-scoped preference picks
 * (preferring the per-rodeo attachment row over the legacy group-level
 * columns) and a per-member breakdown for the expandable view.
 */
export interface UpcomingBuddyGroup {
  id: string;
  group_name: string;
  is_primary: boolean;
  member_count: number;
  status: string;
  preferences_first: string | null;
  preferences_second: string | null;
  preferences_locked_at: string | null;
  members: UpcomingBuddyGroupMember[];
}

export interface UpcomingRodeoEntry {
  rodeo: {
    id: string;
    name: string | null;
    start_date: string | null;
    end_date: string | null;
    entry_close_time: string | null;
    location?: string | null;
    rodeo_or_jackpot?: string | null;
    /**
     * IANA timezone the rodeo's wall-clock fields are anchored to (e.g.
     * "America/Los_Angeles"). Drives the dashboard's timezone-aware
     * formatters — entry-close time, preference datetimes — so every
     * viewer sees the time the host configured plus a 2-letter US zone
     * abbreviation appended (PT/MT/CT/ET).
     */
    timezone?: string | null;
  } | null;
  registrations: UpcomingEvent[];
  buddy_groups: UpcomingBuddyGroup[];
}

/**
 * One past entry as returned by /dashboard-data/my/event-history.
 *
 * Only entries the contestant was actually scored in reach this list, so
 * every row is a result: where they placed, what they won, and the day they
 * performed. Entry fees are not part of it — that's Profile → Financials.
 */
export interface EventHistoryEntry {
  id: string;
  registration_id: string;
  event_id: string | null;
  event_name: string;
  rodeo_id: string | null;
  rodeo_name: string | null;
  organization_name: string | null;
  /**
   * When they competed: the performance's scheduled start (a UTC instant, to
   * be rendered in `timezone`), or the rodeo's bare start date when the
   * performance had no scheduled time.
   */
  date: string | null;
  /** IANA zone `date` is anchored to — the organizer's wall clock. */
  timezone: string | null;
  end_date: string | null;
  location: string | null;
  status: string;
  entry_fee: number;
  transaction_fee: number;
  /** Where they finished. null = unplaced, or results not revealed yet. */
  placement: number | null;
  /** Scored, but they didn't compete — takes no placement by definition. */
  did_not_perform: boolean;
  /**
   * THIS contestant's prize money — their SHARE of the entry's payout, which
   * is half of a team roping win. null when there is none, or it's withheld.
   */
  winnings: number | null;
  /**
   * The undivided team total when the entry's prize was shared (team roping),
   * so the row can show the breakdown. null for a solo entry, where the
   * contestant's share IS the total.
   */
  team_winnings: number | null;
  [key: string]: unknown;
}

interface EventHistoryParams {
  year?: number;
  /**
   * 'results' resolves placement + prize money (and the event standings that
   * takes). Callers that only need the count leave this alone.
   */
  view?: 'summary' | 'results';
  /** Hold the request until the caller wants it — e.g. a collapsed section. */
  enabled?: boolean;
}

// --- Query Hooks ---
export function useTotalUsers() {
  return useQuery({
    queryKey: dashboardDataKeys.totalUsers(),
    queryFn: () =>
      httpClient.get<TotalUsersData>('/dashboard-data/stats/total-users'),
  });
}

export function useActiveOrganizations() {
  return useQuery({
    queryKey: dashboardDataKeys.activeOrganizations(),
    queryFn: () =>
      httpClient.get<ActiveOrganizationsData>(
        '/dashboard-data/stats/active-organizations',
      ),
  });
}

export function useSystemHealth() {
  return useQuery({
    queryKey: dashboardDataKeys.systemHealth(),
    queryFn: () =>
      httpClient.get<SystemHealthData>('/dashboard-data/stats/system-health'),
  });
}

export function useActiveJackpots() {
  return useQuery({
    queryKey: dashboardDataKeys.activeJackpots(),
    queryFn: () =>
      httpClient.get<ActiveJackpotsData>(
        '/dashboard-data/stats/active-jackpots',
      ),
  });
}

export function useRecentEvents(days?: number) {
  const query = days ? `?days=${days}` : '';
  return useQuery({
    queryKey: dashboardDataKeys.recentEvents(days),
    queryFn: () =>
      httpClient.get<RecentEvent[]>(
        `/dashboard-data/stats/recent-events${query}`,
      ),
  });
}

export function useMemberStatistics() {
  return useQuery({
    queryKey: dashboardDataKeys.memberStatistics(),
    queryFn: () =>
      httpClient.get<MemberStatistics>(
        '/dashboard-data/stats/member-statistics',
      ),
  });
}

export function useMyUpcomingEvents() {
  return useQuery({
    queryKey: dashboardDataKeys.myUpcomingEvents(),
    queryFn: async (): Promise<UpcomingEvent[]> => {
      const raw = await httpClient.get<{ events: UpcomingEvent[] }>(
        '/dashboard-data/my/upcoming-events',
      );
      return raw.events;
    },
  });
}

/**
 * Unified "upcoming rodeos" feed for the dashboard. One entry per rodeo
 * the user has any current involvement in — directly via event
 * registrations, indirectly via buddy_group_rodeo_attachments through
 * their buddy_group_members rows, or both. Each entry includes the
 * rodeo header (start/end dates, entry_close_time, location), the user's
 * individual event registrations under that rodeo, and the buddy groups
 * they belong to that are attached to it.
 */
export function useMyUpcomingRodeos() {
  return useQuery({
    queryKey: dashboardDataKeys.myUpcomingRodeos(),
    queryFn: async (): Promise<UpcomingRodeoEntry[]> => {
      const raw = await httpClient.get<{ rodeos: UpcomingRodeoEntry[] }>(
        '/dashboard-data/my/upcoming-rodeos',
      );
      return raw.rodeos;
    },
  });
}

/**
 * Rodeo whose `entry_close_time` is within the next N days (default 3).
 * Surfaced via /dashboard-data/my/closing-soon and drives both the
 * dashboard banner and the compact header indicator. Returned sorted by
 * soonest-closing first so the hero slot can take[0].
 */
export interface ClosingRodeo {
  id: string;
  name: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_close_time: string;
  location: string | null;
  rodeo_or_jackpot: string | null;
  timezone: string | null;
}

/**
 * Rodeos in the user's organizations whose entry close time is within
 * the next `days` window (default 3). Short staleTime + 30s refetch
 * interval so the countdown view freshens without aggressive polling;
 * the per-tick countdown lives in <CountdownTimer>, which ticks every
 * second locally without re-hitting the API.
 */
export function useMyClosingRodeos(days?: number) {
  const query = days ? `?days=${days}` : '';
  return useQuery({
    queryKey: dashboardDataKeys.myClosingRodeos(days),
    queryFn: async (): Promise<ClosingRodeo[]> => {
      const raw = await httpClient.get<{ rodeos: ClosingRodeo[] }>(
        `/dashboard-data/my/closing-soon${query}`,
      );
      return raw.rodeos;
    },
    // 60s stale time — entries close on the order of hours/days, so we
    // don't need server data faster than that. The component handles
    // per-second display updates locally.
    staleTime: 60 * 1000,
    // Periodic background refetch so a rodeo that crosses its close-time
    // mid-session falls out of the list without a manual refresh.
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * Pending team-roping partner invitations awaiting the current user's
 * accept/reject. Backs the prominent dashboard PendingInvitationsCard.
 * Share the `['dashboard-data']` prefix so that `useRespondToPartner`'s
 * blanket invalidate (`['dashboard-data']`) automatically refreshes this
 * card when the user acts from either the dashboard OR the event detail
 * page.
 */
export function useMyPendingInvitations() {
  return useQuery({
    queryKey: dashboardDataKeys.myPendingInvitations(),
    queryFn: async (): Promise<PendingInvitation[]> => {
      const raw = await httpClient.get<{ invitations: PendingInvitation[] }>(
        '/dashboard-data/my/pending-invitations',
      );
      return raw.invitations;
    },
  });
}

/**
 * Every scored entry for the user, unpaginated, plus the winnings total for
 * the selected year.
 *
 * `view: 'results'` is the expensive half — the API computes full standings
 * per distinct event to resolve placements and prize money — so it belongs to
 * the expanded history list only, gated behind `enabled`. The stats card asks
 * for the default 'summary' and gets the count without any of that work.
 */
export function useMyEventHistory(params?: EventHistoryParams) {
  const { enabled = true, ...request } = params ?? {};
  const qs = new URLSearchParams();
  if (request.year) qs.set('year', String(request.year));
  if (request.view) qs.set('view', request.view);
  const query = qs.toString() ? `?${qs}` : '';
  return useQuery({
    queryKey: dashboardDataKeys.myEventHistory(request),
    enabled,
    queryFn: async (): Promise<{
      data: EventHistoryEntry[];
      total: number;
      totalWinnings: number | null;
    }> => {
      const raw = await httpClient.get<{
        events: EventHistoryEntry[];
        total: number;
        total_winnings: number | null;
      }>(`/dashboard-data/my/event-history${query}`);
      return { data: raw.events, total: raw.total, totalWinnings: raw.total_winnings };
    },
  });
}
